import axios from 'axios'

export interface PortItem {
  id: number
  main_port: string
  latitude?: number
  longitude?: number
}

interface PortsCachePayload {
  timestamp: number
  data: PortItem[]
}

const CACHE_KEY = 'shipedge_cached_ports_v1'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

const BASE_API_URL =
  process.env.REACT_APP_API_URL || 'https://fleetdoctor.uat.api.payplatter.in/api'
const PORTS_ALL_URL = `${BASE_API_URL.replace(/\/+$/, '')}/ports/all`

let memoryCache: PortItem[] | null = null
let inFlightPromise: Promise<PortItem[]> | null = null

/**
 * Reads valid ports from localStorage if available and not expired.
 */
function readFromLocalStorage(): PortItem[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null

    const parsed: PortsCachePayload = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.data)) return null

    // Check if expired
    const isExpired = Date.now() - parsed.timestamp > CACHE_TTL_MS
    if (isExpired) {
      return null
    }

    return parsed.data
  } catch (err) {
    console.warn('[portService] Failed to read cached ports from localStorage:', err)
    return null
  }
}

/**
 * Saves ports into localStorage.
 */
function saveToLocalStorage(data: PortItem[]): void {
  try {
    const payload: PortsCachePayload = {
      timestamp: Date.now(),
      data,
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
  } catch (err) {
    console.warn('[portService] Failed to save ports to localStorage:', err)
  }
}

/**
 * Synchronous read to immediately pre-fill options if available (0ms).
 */
export function getCachedPortsSync(): PortItem[] | null {
  if (memoryCache && memoryCache.length > 0) {
    return memoryCache
  }
  const localData = readFromLocalStorage()
  if (localData && localData.length > 0) {
    memoryCache = localData
    return localData
  }
  return null
}

/**
 * Fetches all ports from API with caching and in-flight request deduplication.
 */
export async function fetchAllPorts(forceRefresh = false): Promise<PortItem[]> {
  // 1. Return in-memory cache if available
  if (!forceRefresh && memoryCache && memoryCache.length > 0) {
    return memoryCache
  }

  // 2. Return localStorage cache if available
  if (!forceRefresh) {
    const localData = readFromLocalStorage()
    if (localData && localData.length > 0) {
      memoryCache = localData
      return localData
    }
  }

  // 3. Deduplicate simultaneous in-flight requests
  if (inFlightPromise) {
    return inFlightPromise
  }

  inFlightPromise = (async () => {
    try {
      const response = await axios.get<any>(PORTS_ALL_URL)
      const rawData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.content)
        ? response.data.content
        : []

      const normalized: PortItem[] = rawData
        .filter((item: any) => item && (item.main_port || item.name))
        .map((item: any) => ({
          id: Number(item.id),
          main_port: String(item.main_port || item.name || '').trim(),
          latitude: typeof item.latitude === 'number' ? item.latitude : undefined,
          longitude: typeof item.longitude === 'number' ? item.longitude : undefined,
        }))
        // Filter out empty port names
        .filter((p) => p.main_port.length > 0)

      memoryCache = normalized
      saveToLocalStorage(normalized)
      return normalized
    } catch (error) {
      console.error('[portService] Failed to fetch ports from API:', error)
      // If network fails, fallback to stale localStorage if present
      try {
        const staleRaw = localStorage.getItem(CACHE_KEY)
        if (staleRaw) {
          const staleParsed: PortsCachePayload = JSON.parse(staleRaw)
          if (Array.isArray(staleParsed.data) && staleParsed.data.length > 0) {
            console.warn('[portService] Using stale cached ports as fallback.')
            memoryCache = staleParsed.data
            return staleParsed.data
          }
        }
      } catch {
        // ignore
      }
      throw error
    } finally {
      inFlightPromise = null
    }
  })()

  return inFlightPromise
}

/**
 * Clears ports cache from memory and localStorage.
 */
export function clearPortsCache(): void {
  memoryCache = null
  inFlightPromise = null
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch {
    // ignore
  }
}
